import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { Horizontal, SBHorizontal } from 'atoms/layout/flex';
import { FollowButton } from 'components/favorites/FollowButton';
import { SubscribersCount } from 'components/favorites/SubscribersCount';
import { isType } from 'lib/gql';
import { laptopAndAbove } from 'style/mediaQuery';

import { NotificationPreference } from './NotificationPreference';
import { PrivacyPolicyDialog } from './PrivacyPolicyDialog';
import type {
  Follower_dataPartner,
  Follower_subscription,
} from './__generated__/index.graphql';

const Root = styled(SBHorizontal).attrs({ gap: 2 })`
  @media ${laptopAndAbove} {
    justify-content: flex-end;
  }
`;

type Props = {
  subscription: Follower_subscription;
  dataPartner?: Follower_dataPartner | null;
  hideCount?: boolean;
};

export const Follower = ({ subscription, dataPartner, hideCount }: Props) => {
  const [showPrivacyPolicyDialog, setShowPrivacyPolicyDialog] =
    useState<boolean>(false);

  /** Update the count locally for instant feedback, even when you are logged out.
   * FollowButton will then execute the mutation when the user log in either after
   * a signin ou a signup.
   */
  const [subscriptionsCount, setSubscriptionsCount] = useState(
    subscription.subscriptionsCount
  );

  const canChooseRarities =
    isType(subscription, 'Player') ||
    isType(subscription, 'NBAPlayer') ||
    isType(subscription, 'BaseballPlayer');

  const onChange = (value: boolean) => {
    if (dataPartner && value) setShowPrivacyPolicyDialog(true);
  };

  return (
    <Root>
      {!hideCount && <SubscribersCount count={subscriptionsCount} />}
      <Horizontal gap={2}>
        {!!canChooseRarities && !!subscription.currentUserSubscription && (
          <NotificationPreference
            subscription={subscription.currentUserSubscription}
          />
        )}
        <FollowButton
          currentUserSubscription={subscription.currentUserSubscription}
          subscribable={subscription}
          onChange={onChange}
          onClick={(value: boolean) => {
            setSubscriptionsCount(c => c + (value ? 1 : -1));
          }}
        />
        {!!dataPartner && !!showPrivacyPolicyDialog && (
          <PrivacyPolicyDialog
            dataPartner={dataPartner}
            onClose={() => setShowPrivacyPolicyDialog(false)}
          />
        )}
      </Horizontal>
    </Root>
  );
};

Follower.fragments = {
  subscription: gql`
    fragment Follower_subscription on WithSubscriptionsInterface {
      slug
      subscriptionsCount
      currentUserSubscription {
        slug
        ...NotificationPreference_subscription
      }
    }
    ${NotificationPreference.fragments.subscription}
  ` as TypedDocumentNode<Follower_subscription>,
  dataPartner: gql`
    fragment Follower_dataPartner on DataPartner {
      slug
      ...PrivacyPolicyDialog_dataPartner
    }
    ${PrivacyPolicyDialog.fragments.dataPartner}
  ` as TypedDocumentNode<Follower_dataPartner>,
};
